import { create } from 'zustand'
import { onAuthStateChanged, signOut } from 'firebase/auth'
import { auth, USE_FIREBASE } from '../config/firebase'
import axios from '../config/axios'

export const useAuthStore = create((set) => ({
  user: null,
  isAuthenticated: false,
  isApproved: false,
  isDeclined: false,
  isAdmin: false,
  loading: false,
  error: null,

  initAuth: () => {
    return new Promise((resolve) => {
      if (!USE_FIREBASE) {
        // Dev mode: use the mock currentUser (if present)
        const user = auth && auth.currentUser ? auth.currentUser : null
        if (user) {
          set({
            user: {
              uid: user.uid,
              email: user.email,
              displayName: user.displayName || ''
            },
            isAuthenticated: true
          })
        } else {
          set({ user: null, isAuthenticated: false })
        }
        resolve()
        return
      }

      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (user) {
          let extra = {}
          try {
            const { doc, getDoc } = await import('firebase/firestore')
            const { db } = await import('../config/firebase')
            const snap = await getDoc(doc(db, 'users', user.uid))
            if (snap.exists()) extra = snap.data()
          } catch (e) { console.error('Firestore read failed:', e.message) }
          // Update lastLogin every time a session is restored (new device, new tab, etc.)
          axios.post('/api/auth/reset-attempts', { identifier: user.email, uid: user.uid }).catch(() => {})
          if (extra.blocked === true) {
            await signOut(auth)
            set({ user: null, isAuthenticated: false })
            unsubscribe()
            resolve()
            return
          }
          set({
            user: {
              uid: user.uid,
              email: user.email,
              displayName: user.displayName,
              companyName: extra.companyName || user.displayName,
              cellNumber: extra.cellNumber || '',
              approved: extra.approved ?? false,
              declined: extra.declined ?? false,
              allowedManufacturers: extra.allowedManufacturers || [],
              role: extra.role || ''
            },
            isAuthenticated: true,
            isApproved: extra.approved === true,
            isDeclined: extra.declined === true,
            isAdmin: extra.role === 'admin'
          })
        } else {
          set({ user: null, isAuthenticated: false })
        }
        unsubscribe()
        resolve()
      })
    })
  },

  login: async (emailOrCell, pin) => {
    set({ loading: true, error: null })
    try {
      let email = emailOrCell.trim()

      // If input looks like a phone number, look up the email from the backend
      if (!/\S+@\S+\.\S+/.test(email)) {
        const res = await axios.post('/api/auth/lookup-by-cell', { cellNumber: email })
        email = res.data.email
      }

      const identifier = email.toLowerCase().trim()

      // Check lockout before attempting Firebase login
      const lockoutRes = await axios.post('/api/auth/check-lockout', { identifier })
      if (lockoutRes.data.locked) {
        const err = new Error(`LOCKED:${lockoutRes.data.lockedUntil}`)
        set({ loading: false })
        throw err
      }

      let result
      try {
        const { signInWithEmailAndPassword } = await import('firebase/auth')
        result = await signInWithEmailAndPassword(auth, email, pin)
      } catch (firebaseError) {
        // Record failed attempt — fetch user info for alert email if available
        let userInfo = null
        try {
          const { doc, getDoc } = await import('firebase/firestore')
          const { db } = await import('../config/firebase')
          const snap = await db ? getDoc(doc(db, 'users', identifier)) : null
          if (snap?.exists()) userInfo = snap.data()
        } catch (_) {}
        await axios.post('/api/auth/record-failed-attempt', { identifier, userInfo }).catch(() => {})
        set({ loading: false })
        throw firebaseError
      }

      // Success — reset attempt counter and update lastLogin (server-side)
      await axios.post('/api/auth/reset-attempts', { identifier, uid: result.user.uid }).catch(() => {})

      let extra = {}
      try {
        const { doc, getDoc } = await import('firebase/firestore')
        const { db } = await import('../config/firebase')
        const snap = await getDoc(doc(db, 'users', result.user.uid))
        if (snap.exists()) extra = snap.data()
      } catch (e) { console.error('Firestore read failed in login:', e.message) }

      if (extra.blocked === true) {
        await signOut(auth)
        set({ loading: false })
        throw new Error('Your account has been blocked. Please contact support.')
      }

      set({
        user: {
          uid: result.user.uid,
          email: result.user.email,
          displayName: result.user.displayName,
          companyName: extra.companyName || result.user.displayName,
          cellNumber: extra.cellNumber || '',
          approved: extra.approved ?? false,
          declined: extra.declined ?? false,
          allowedManufacturers: extra.allowedManufacturers || [],
          role: extra.role || ''
        },
        isAuthenticated: true,
        isApproved: extra.approved === true,
        isDeclined: extra.declined === true,
        isAdmin: extra.role === 'admin',
        loading: false
      })
    } catch (error) {
      const msg = error.response?.data?.error || error.message
      set({ error: msg, loading: false })
      throw error
    }
  },

  register: async (email, pin, companyName, cellNumber, firstName, lastName) => {
    set({ loading: true, error: null })
    try {
      const { createUserWithEmailAndPassword, updateProfile } = await import('firebase/auth')
      const { doc, setDoc } = await import('firebase/firestore')
      const result = await createUserWithEmailAndPassword(auth, email, pin)
      const fullName = `${firstName} ${lastName}`.trim()
      await updateProfile(result.user, { displayName: fullName })

      // Save extra profile fields to Firestore
      const { db } = await import('../config/firebase')
      const cellNormalized = cellNumber.replace(/\D/g, '').slice(-9)
      await setDoc(doc(db, 'users', result.user.uid), {
        email,
        firstName,
        lastName,
        fullName: `${firstName} ${lastName}`.trim(),
        companyName,
        cellNumber,
        cellNormalized,
        approved: false,
        allowedManufacturers: [],
        createdAt: new Date().toISOString()
      })

      set({
        user: {
          uid: result.user.uid,
          email: result.user.email,
          displayName: companyName,
          companyName,
          cellNumber,
          approved: false
        },
        isAuthenticated: true,
        isApproved: false,
        loading: false
      })

      // Notify admins of new signup (non-blocking)
      axios.post('/api/auth/notify-signup', { uid: result.user.uid, email, firstName, lastName, companyName, cellNumber }).catch(() => {})
    } catch (error) {
      set({ error: error.message, loading: false })
      throw error
    }
  },

  logout: async () => {
    try {
      await signOut(auth)
      set({ user: null, isAuthenticated: false, error: null })
    } catch (error) {
      set({ error: error.message })
    }
  },

  clearError: () => set({ error: null })
}))
